/**
 * @module
 * A typesafe validation & parsing library for TypeScript.
 *
 * @example
 * ```ts
 * import * as v from "@badrap/valita";
 *
 * const vehicle = v.union(
 *   v.object({ type: v.literal("plane"), airline: v.string() }),
 *   v.object({ type: v.literal("train") }),
 *   v.object({ type: v.literal("automobile"), make: v.string() })
 * );
 * vehicle.parse({ type: "bike" });
 * // ValitaError: invalid_literal at .type (expected "plane", "train" or "automobile")
 * ```
 */
function joinIssues(left, right) {
    return left ? { ok: false, code: "join", left, right } : right;
}
function prependPath(key, tree) {
    return { ok: false, code: "prepend", key, tree };
}
function cloneIssueWithPath(tree, path) {
    const code = tree.code;
    switch (code) {
        case "invalid_type":
            return { code, path, expected: tree.expected };
        case "invalid_literal":
            return { code, path, expected: tree.expected };
        case "missing_value":
            return { code, path };
        case "invalid_length":
            return {
                code,
                path,
                minLength: tree.minLength,
                maxLength: tree.maxLength,
            };
        case "unrecognized_keys":
            return { code, path, keys: tree.keys };
        case "invalid_union":
            return { code, path, tree: tree.tree };
        case "custom_error":
            return { code, path, error: tree.error };
    }
}
function collectIssues(tree, path = [], issues = []) {
    for (;;) {
        if (tree.code === "join") {
            collectIssues(tree.left, path.slice(), issues);
            tree = tree.right;
        }
        else if (tree.code === "prepend") {
            path.push(tree.key);
            tree = tree.tree;
        }
        else {
            if (tree.code === "custom_error" &&
                typeof tree.error === "object" &&
                tree.error.path !== undefined) {
                path.push(...tree.error.path);
            }
            issues.push(cloneIssueWithPath(tree, path));
            return issues;
        }
    }
}
function separatedList(list, sep) {
    if (list.length === 0) {
        return "nothing";
    }
    else if (list.length === 1) {
        return list[0];
    }
    else {
        return `${list.slice(0, -1).join(", ")} ${sep} ${list[list.length - 1]}`;
    }
}
function formatLiteral(value) {
    return typeof value === "bigint" ? `${value}n` : JSON.stringify(value);
}
function countIssues(tree) {
    let count = 0;
    for (;;) {
        if (tree.code === "join") {
            count += countIssues(tree.left);
            tree = tree.right;
        }
        else if (tree.code === "prepend") {
            tree = tree.tree;
        }
        else {
            return count + 1;
        }
    }
}
function formatIssueTree(tree) {
    let path = "";
    let count = 0;
    for (;;) {
        if (tree.code === "join") {
            count += countIssues(tree.right);
            tree = tree.left;
        }
        else if (tree.code === "prepend") {
            path += `.${tree.key}`;
            tree = tree.tree;
        }
        else {
            break;
        }
    }
    let message = "validation failed";
    if (tree.code === "invalid_type") {
        message = `expected ${separatedList(tree.expected, "or")}`;
    }
    else if (tree.code === "invalid_literal") {
        message = `expected ${separatedList(tree.expected.map(formatLiteral), "or")}`;
    }
    else if (tree.code === "missing_value") {
        message = `missing value`;
    }
    else if (tree.code === "unrecognized_keys") {
        const keys = tree.keys;
        message = `unrecognized ${keys.length === 1 ? "key" : "keys"} ${separatedList(keys.map(formatLiteral), "and")}`;
    }
    else if (tree.code === "invalid_length") {
        const min = tree.minLength;
        const max = tree.maxLength;
        message = `expected an array with `;
        if (min > 0) {
            if (max === min) {
                message += `${min}`;
            }
            else if (max !== undefined) {
                message += `between ${min} and ${max}`;
            }
            else {
                message += `at least ${min}`;
            }
        }
        else {
            message += `at most ${max !== null && max !== void 0 ? max : "∞"}`;
        }
        message += ` item(s)`;
    }
    else if (tree.code === "custom_error") {
        const error = tree.error;
        if (typeof error === "string") {
            message = error;
        }
        else if (error !== undefined) {
            if (error.message !== undefined) {
                message = error.message;
            }
            if (error.path !== undefined) {
                path += "." + error.path.join(".");
            }
        }
    }
    let msg = `${tree.code} at .${path.slice(1)} (${message})`;
    if (count === 1) {
        msg += ` (+ 1 other issue)`;
    }
    else if (count > 1) {
        msg += ` (+ ${count} other issues)`;
    }
    return msg;
}
/**
 * An error type representing one or more validation/parsing errors.
 *
 * The `.message` property gives a short overview of the encountered issues,
 * while the `.issue` property can be used to get a more detailed list.
 *
 * @example
 * ```ts
 * const t = v.object({ a: v.null(), b: v.null() });
 *
 * try {
 *   t.parse({ a: 1 });
 * } catch (err) {
 *   err.message;
 *   // "invalid_type at .a (expected null) (+ 1 other issue)"
 *
 *   err.issues;
 *   // [
 *   //   { code: 'invalid_type', path: [ 'a' ], expected: [ 'null' ] },
 *   //   { code: 'missing_value', path: [ 'b' ] }
 *   // ]
 * }
 * ```
 */
export class ValitaError extends Error {
    constructor(issueTree) {
        super(formatIssueTree(issueTree));
        this.issueTree = issueTree;
        Object.setPrototypeOf(this, new.target.prototype);
        this.name = new.target.name;
        this._issues = undefined;
    }
    get issues() {
        if (this._issues === undefined) {
            this._issues = collectIssues(this.issueTree);
        }
        return this._issues;
    }
}
class ErrImpl {
    constructor(issueTree) {
        this.issueTree = issueTree;
        this.ok = false;
        this._issues = undefined;
        this._message = undefined;
    }
    get issues() {
        if (this._issues === undefined) {
            this._issues = collectIssues(this.issueTree);
        }
        return this._issues;
    }
    get message() {
        if (this._message === undefined) {
            this._message = formatIssueTree(this.issueTree);
        }
        return this._message;
    }
    throw() {
        throw new ValitaError(this.issueTree);
    }
}
function ok(value) {
    return { ok: true, value };
}
/**
 * Create a value for returning a parsing error from chain().
 *
 * An optional error message can be provided.
 *
 * @example
 * ```ts
 * const t = v.string().chain(() => v.err("bad value"));
 *
 * t.parse("hello");
 * // ValitaError: custom_error at . (bad value)
 * ```
 */
function err(error) {
    return new ErrImpl({ ok: false, code: "custom_error", error });
}
function isObject(v) {
    return typeof v === "object" && v !== null && !Array.isArray(v);
}
const FLAG_FORBID_EXTRA_KEYS = 0x1;
const FLAG_STRIP_EXTRA_KEYS = 0x2;
const FLAG_MISSING_VALUE = 0x4;
class AbstractType {
    optional(defaultFn) {
        const optional = new Optional(this);
        if (!defaultFn) {
            return optional;
        }
        return new TransformType(optional, (v) => {
            return v === undefined ? { ok: true, value: defaultFn() } : undefined;
        });
    }
    default(defaultValue) {
        const defaultResult = ok(defaultValue);
        return new TransformType(this.optional(), (v) => {
            return v === undefined ? defaultResult : undefined;
        });
    }
    assert(func, error) {
        const err = { ok: false, code: "custom_error", error };
        return new TransformType(this, (v, options) => func(v, options) ? undefined : err);
    }
    map(func) {
        return new TransformType(this, (v, options) => ({
            ok: true,
            value: func(v, options),
        }));
    }
    chain(func) {
        return new TransformType(this, (v, options) => {
            const r = func(v, options);
            return r.ok ? r : r.issueTree;
        });
    }
}
/**
 * A base class for all concreate validators/parsers.
 */
class Type extends AbstractType {
    /**
     * Return new validator that accepts both the original type and `null`.
     */
    nullable() {
        return new Nullable(this);
    }
    toTerminals(func) {
        func(this);
    }
    /**
     * Parse a value without throwing.
     */
    try(v, options) {
        let flags = FLAG_FORBID_EXTRA_KEYS;
        if ((options === null || options === void 0 ? void 0 : options.mode) === "passthrough") {
            flags = 0;
        }
        else if ((options === null || options === void 0 ? void 0 : options.mode) === "strip") {
            flags = FLAG_STRIP_EXTRA_KEYS;
        }
        const r = this.func(v, flags);
        if (r === undefined) {
            return { ok: true, value: v };
        }
        else if (r.ok) {
            return { ok: true, value: r.value };
        }
        else {
            return new ErrImpl(r);
        }
    }
    /**
     * Parse a value. Throw a ValitaError on failure.
     */
    parse(v, options) {
        let flags = FLAG_FORBID_EXTRA_KEYS;
        if ((options === null || options === void 0 ? void 0 : options.mode) === "passthrough") {
            flags = 0;
        }
        else if ((options === null || options === void 0 ? void 0 : options.mode) === "strip") {
            flags = FLAG_STRIP_EXTRA_KEYS;
        }
        const r = this.func(v, flags);
        if (r === undefined) {
            return v;
        }
        else if (r.ok) {
            return r.value;
        }
        else {
            throw new ValitaError(r);
        }
    }
}
class Nullable extends Type {
    constructor(type) {
        super();
        this.type = type;
        this.name = "nullable";
    }
    func(v, flags) {
        return v === null ? undefined : this.type.func(v, flags);
    }
    toTerminals(func) {
        func(nullSingleton);
        this.type.toTerminals(func);
    }
    nullable() {
        return this;
    }
}
/**
 * A validator/parser marked as "optional", signifying that their value can
 * be missing from the parsed object.
 *
 * As such optionals can only be used as property validators within
 * object validators.
 */
class Optional extends AbstractType {
    constructor(type) {
        super();
        this.type = type;
        this.name = "optional";
    }
    func(v, flags) {
        return v === undefined || flags & FLAG_MISSING_VALUE
            ? undefined
            : this.type.func(v, flags);
    }
    toTerminals(func) {
        func(this);
        func(undefinedSingleton);
        this.type.toTerminals(func);
    }
    optional(defaultFn) {
        if (!defaultFn) {
            return this;
        }
        return new TransformType(this, (v) => {
            return v === undefined ? { ok: true, value: defaultFn() } : undefined;
        });
    }
}
// Set a bit in position `index` to one and return the updated bitset.
// This function may or may not mutate `bits` in-place.
function setBit(bits, index) {
    if (typeof bits !== "number") {
        const idx = index >> 5;
        for (let i = bits.length; i <= idx; i++) {
            bits.push(0);
        }
        bits[idx] |= 1 << index % 32;
        return bits;
    }
    else if (index < 32) {
        return bits | (1 << index);
    }
    else {
        return setBit([bits, 0], index);
    }
}
// Get the bit at position `index`.
function getBit(bits, index) {
    if (typeof bits === "number") {
        return index < 32 ? (bits >>> index) & 1 : 0;
    }
    else {
        return (bits[index >> 5] >>> index % 32) & 1;
    }
}
class ObjectType extends Type {
    constructor(shape, restType, checks) {
        super();
        this.shape = shape;
        this.restType = restType;
        this.checks = checks;
        this.name = "object";
        this._invalidType = {
            ok: false,
            code: "invalid_type",
            expected: ["object"],
        };
    }
    check(func, error) {
        var _a;
        const issue = { ok: false, code: "custom_error", error };
        return new ObjectType(this.shape, this.restType, [
            ...((_a = this.checks) !== null && _a !== void 0 ? _a : []),
            {
                func: func,
                issue,
            },
        ]);
    }
    func(v, flags) {
        if (!isObject(v)) {
            return this._invalidType;
        }
        let func = this._func;
        if (func === undefined) {
            func = createObjectMatcher(this.shape, this.restType, this.checks);
            this._func = func;
        }
        return func(v, flags);
    }
    rest(restType) {
        return new ObjectType(this.shape, restType);
    }
    extend(shape) {
        return new ObjectType(Object.assign(Object.assign({}, this.shape), shape), this.restType);
    }
    pick(...keys) {
        const shape = {};
        keys.forEach((key) => {
            shape[key] = this.shape[key];
        });
        return new ObjectType(shape, undefined);
    }
    omit(...keys) {
        const shape = Object.assign({}, this.shape);
        keys.forEach((key) => {
            delete shape[key];
        });
        return new ObjectType(shape, this.restType);
    }
    partial() {
        var _a;
        const shape = {};
        Object.keys(this.shape).forEach((key) => {
            shape[key] = this.shape[key].optional();
        });
        const rest = (_a = this.restType) === null || _a === void 0 ? void 0 : _a.optional();
        return new ObjectType(shape, rest);
    }
}
function createObjectMatcher(shape, rest, checks) {
    const requiredKeys = [];
    const optionalKeys = [];
    for (const key in shape) {
        let hasOptional = false;
        shape[key].toTerminals((t) => {
            hasOptional || (hasOptional = t.name === "optional");
        });
        if (hasOptional) {
            optionalKeys.push(key);
        }
        else {
            requiredKeys.push(key);
        }
    }
    const keys = [...requiredKeys, ...optionalKeys];
    const totalCount = keys.length;
    if (totalCount === 0 && (rest === null || rest === void 0 ? void 0 : rest.name) === "unknown") {
        // A fast path for record(unknown())
        return function (obj, _) {
            if (checks !== undefined) {
                for (let i = 0; i < checks.length; i++) {
                    if (!checks[i].func(obj)) {
                        return checks[i].issue;
                    }
                }
            }
            return undefined;
        };
    }
    const types = keys.map((key) => shape[key]);
    const requiredCount = requiredKeys.length;
    const invertedIndexes = Object.create(null);
    keys.forEach((key, index) => {
        invertedIndexes[key] = ~index;
    });
    const missingValues = requiredKeys.map((key) => prependPath(key, {
        ok: false,
        code: "missing_value",
    }));
    function set(obj, key, value) {
        if (key === "__proto__") {
            Object.defineProperty(obj, key, {
                value,
                writable: true,
                enumerable: true,
                configurable: true,
            });
        }
        else {
            obj[key] = value;
        }
    }
    return function (obj, flags) {
        let copied = false;
        let output = obj;
        let issues;
        let unrecognized = undefined;
        let seenBits = 0;
        let seenCount = 0;
        if (flags & FLAG_FORBID_EXTRA_KEYS ||
            flags & FLAG_STRIP_EXTRA_KEYS ||
            rest !== undefined) {
            for (const key in obj) {
                const value = obj[key];
                const index = ~invertedIndexes[key];
                let r;
                if (index >= 0) {
                    seenCount++;
                    seenBits = setBit(seenBits, index);
                    r = types[index].func(value, flags);
                }
                else if (rest !== undefined) {
                    r = rest.func(value, flags);
                }
                else {
                    if (flags & FLAG_FORBID_EXTRA_KEYS) {
                        if (unrecognized === undefined) {
                            unrecognized = [key];
                        }
                        else {
                            unrecognized.push(key);
                        }
                    }
                    else if (flags & FLAG_STRIP_EXTRA_KEYS &&
                        issues === undefined &&
                        !copied) {
                        output = {};
                        copied = true;
                        for (let m = 0; m < totalCount; m++) {
                            if (getBit(seenBits, m)) {
                                const k = keys[m];
                                set(output, k, obj[k]);
                            }
                        }
                    }
                    continue;
                }
                if (r === undefined) {
                    if (copied && issues === undefined) {
                        set(output, key, value);
                    }
                }
                else if (!r.ok) {
                    issues = joinIssues(issues, prependPath(key, r));
                }
                else if (issues === undefined) {
                    if (!copied) {
                        output = {};
                        copied = true;
                        if (rest === undefined) {
                            for (let m = 0; m < totalCount; m++) {
                                if (m !== index && getBit(seenBits, m)) {
                                    const k = keys[m];
                                    set(output, k, obj[k]);
                                }
                            }
                        }
                        else {
                            for (const k in obj) {
                                set(output, k, obj[k]);
                            }
                        }
                    }
                    set(output, key, r.value);
                }
            }
        }
        if (seenCount < totalCount) {
            for (let i = 0; i < totalCount; i++) {
                if (getBit(seenBits, i)) {
                    continue;
                }
                const key = keys[i];
                const value = obj[key];
                let keyFlags = flags & ~FLAG_MISSING_VALUE;
                if (value === undefined && !(key in obj)) {
                    if (i < requiredCount) {
                        issues = joinIssues(issues, missingValues[i]);
                        continue;
                    }
                    keyFlags |= FLAG_MISSING_VALUE;
                }
                const r = types[i].func(value, keyFlags);
                if (r === undefined) {
                    if (copied &&
                        issues === undefined &&
                        !(keyFlags & FLAG_MISSING_VALUE)) {
                        set(output, key, value);
                    }
                }
                else if (!r.ok) {
                    issues = joinIssues(issues, prependPath(key, r));
                }
                else if (issues === undefined) {
                    if (!copied) {
                        output = {};
                        copied = true;
                        if (rest === undefined) {
                            for (let m = 0; m < totalCount; m++) {
                                if (m < i || getBit(seenBits, m)) {
                                    const k = keys[m];
                                    set(output, k, obj[k]);
                                }
                            }
                        }
                        else {
                            for (const k in obj) {
                                set(output, k, obj[k]);
                            }
                            for (let m = 0; m < i; m++) {
                                if (!getBit(seenBits, m)) {
                                    const k = keys[m];
                                    set(output, k, obj[k]);
                                }
                            }
                        }
                    }
                    set(output, key, r.value);
                }
            }
        }
        if (unrecognized !== undefined) {
            issues = joinIssues(issues, {
                ok: false,
                code: "unrecognized_keys",
                keys: unrecognized,
            });
        }
        if (issues === undefined && checks !== undefined) {
            for (let i = 0; i < checks.length; i++) {
                if (!checks[i].func(output)) {
                    return checks[i].issue;
                }
            }
        }
        if (issues === undefined && copied) {
            return { ok: true, value: output };
        }
        else {
            return issues;
        }
    };
}
class ArrayOrTupleType extends Type {
    constructor(prefix, rest, suffix) {
        super();
        this.prefix = prefix;
        this.rest = rest;
        this.suffix = suffix;
        this.name = "array";
        this.restType = rest !== null && rest !== void 0 ? rest : never();
        this.minLength = this.prefix.length + this.suffix.length;
        this.maxLength = rest ? undefined : this.minLength;
        this.invalidType = {
            ok: false,
            code: "invalid_type",
            expected: ["array"],
        };
        this.invalidLength = {
            ok: false,
            code: "invalid_length",
            minLength: this.minLength,
            maxLength: this.maxLength,
        };
    }
    func(arr, flags) {
        var _a;
        if (!Array.isArray(arr)) {
            return this.invalidType;
        }
        const length = arr.length;
        const minLength = this.minLength;
        const maxLength = (_a = this.maxLength) !== null && _a !== void 0 ? _a : Infinity;
        if (length < minLength || length > maxLength) {
            return this.invalidLength;
        }
        const headEnd = this.prefix.length;
        const tailStart = arr.length - this.suffix.length;
        let issueTree = undefined;
        let output = arr;
        for (let i = 0; i < arr.length; i++) {
            const type = i < headEnd
                ? this.prefix[i]
                : i >= tailStart
                    ? this.suffix[i - tailStart]
                    : this.restType;
            const r = type.func(arr[i], flags);
            if (r !== undefined) {
                if (r.ok) {
                    if (output === arr) {
                        output = arr.slice();
                    }
                    output[i] = r.value;
                }
                else {
                    issueTree = joinIssues(issueTree, prependPath(i, r));
                }
            }
        }
        if (issueTree) {
            return issueTree;
        }
        else if (arr === output) {
            return undefined;
        }
        else {
            return { ok: true, value: output };
        }
    }
    concat(type) {
        if (this.rest) {
            if (type.rest) {
                throw new TypeError("can not concatenate two variadic types");
            }
            return new ArrayOrTupleType(this.prefix, this.rest, [
                ...this.suffix,
                ...type.prefix,
                ...type.suffix,
            ]);
        }
        else if (type.rest) {
            return new ArrayOrTupleType([...this.prefix, ...this.suffix, ...type.prefix], type.rest, type.suffix);
        }
        else {
            return new ArrayOrTupleType([...this.prefix, ...this.suffix, ...type.prefix, ...type.suffix], type.rest, type.suffix);
        }
    }
}
function toInputType(v) {
    const type = typeof v;
    if (type !== "object") {
        return type;
    }
    else if (v === null) {
        return "null";
    }
    else if (Array.isArray(v)) {
        return "array";
    }
    else {
        return type;
    }
}
function dedup(arr) {
    return Array.from(new Set(arr));
}
function findCommonKeys(rs) {
    const map = new Map();
    rs.forEach((r) => {
        var _a;
        for (const key in r) {
            map.set(key, ((_a = map.get(key)) !== null && _a !== void 0 ? _a : 0) + 1);
        }
    });
    const result = [];
    map.forEach((count, key) => {
        if (count === rs.length) {
            result.push(key);
        }
    });
    return result;
}
function groupTerminals(terminals) {
    const order = new Map();
    const literals = new Map();
    const types = new Map();
    const unknowns = [];
    const optionals = [];
    const expectedTypes = [];
    terminals.forEach(({ root, terminal }) => {
        var _a, _b, _c;
        order.set(root, (_a = order.get(root)) !== null && _a !== void 0 ? _a : order.size);
        if (terminal.name === "never") {
            // skip
        }
        else if (terminal.name === "optional") {
            optionals.push(root);
        }
        else if (terminal.name === "unknown") {
            unknowns.push(root);
        }
        else if (terminal.name === "literal") {
            const roots = (_b = literals.get(terminal.value)) !== null && _b !== void 0 ? _b : [];
            roots.push(root);
            literals.set(terminal.value, roots);
            expectedTypes.push(toInputType(terminal.value));
        }
        else {
            const roots = (_c = types.get(terminal.name)) !== null && _c !== void 0 ? _c : [];
            roots.push(root);
            types.set(terminal.name, roots);
            expectedTypes.push(terminal.name);
        }
    });
    literals.forEach((roots, value) => {
        const options = types.get(toInputType(value));
        if (options) {
            options.push(...roots);
            literals.delete(value);
        }
    });
    const byOrder = (a, b) => {
        var _a, _b;
        return ((_a = order.get(a)) !== null && _a !== void 0 ? _a : 0) - ((_b = order.get(b)) !== null && _b !== void 0 ? _b : 0);
    };
    types.forEach((roots, type) => types.set(type, dedup(roots.concat(unknowns).sort(byOrder))));
    literals.forEach((roots, value) => literals.set(value, dedup(roots.concat(unknowns)).sort(byOrder)));
    return {
        types,
        literals,
        unknowns: dedup(unknowns).sort(byOrder),
        optionals: dedup(optionals).sort(byOrder),
        expectedTypes: dedup(expectedTypes),
    };
}
function createObjectKeyMatcher(objects, key) {
    const list = [];
    for (const { root, terminal } of objects) {
        terminal.shape[key].toTerminals((t) => list.push({ root, terminal: t }));
    }
    const { types, literals, optionals, unknowns, expectedTypes } = groupTerminals(list);
    if (unknowns.length > 0 || optionals.length > 1) {
        return undefined;
    }
    for (const roots of literals.values()) {
        if (roots.length > 1) {
            return undefined;
        }
    }
    for (const roots of types.values()) {
        if (roots.length > 1) {
            return undefined;
        }
    }
    const missingValue = prependPath(key, { ok: false, code: "missing_value" });
    const issue = prependPath(key, types.size === 0
        ? {
            ok: false,
            code: "invalid_literal",
            expected: Array.from(literals.keys()),
        }
        : {
            ok: false,
            code: "invalid_type",
            expected: expectedTypes,
        });
    const litMap = literals.size > 0 ? new Map() : undefined;
    for (const [literal, options] of literals) {
        litMap.set(literal, options[0]);
    }
    const byType = types.size > 0 ? {} : undefined;
    for (const [type, options] of types) {
        byType[type] = options[0];
    }
    return function (_obj, flags) {
        var _a;
        const obj = _obj;
        const value = obj[key];
        if (value === undefined && !(key in obj)) {
            return optionals.length > 0
                ? optionals[0].func(obj, flags)
                : missingValue;
        }
        const option = (_a = byType === null || byType === void 0 ? void 0 : byType[toInputType(value)]) !== null && _a !== void 0 ? _a : litMap === null || litMap === void 0 ? void 0 : litMap.get(value);
        return option ? option.func(obj, flags) : issue;
    };
}
function createUnionObjectMatcher(terminals) {
    if (terminals.some(({ terminal: t }) => t.name === "unknown")) {
        return undefined;
    }
    const objects = terminals.filter((item) => {
        return item.terminal.name === "object";
    });
    if (objects.length < 2) {
        return undefined;
    }
    const shapes = objects.map(({ terminal }) => terminal.shape);
    for (const key of findCommonKeys(shapes)) {
        const matcher = createObjectKeyMatcher(objects, key);
        if (matcher) {
            return matcher;
        }
    }
    return undefined;
}
function createUnionBaseMatcher(terminals) {
    const { expectedTypes, literals, types, unknowns, optionals } = groupTerminals(terminals);
    const issue = types.size === 0 && unknowns.length === 0
        ? {
            ok: false,
            code: "invalid_literal",
            expected: Array.from(literals.keys()),
        }
        : {
            ok: false,
            code: "invalid_type",
            expected: expectedTypes,
        };
    const litMap = literals.size > 0 ? literals : undefined;
    const byType = types.size > 0 ? {} : undefined;
    for (const [type, options] of types) {
        byType[type] = options;
    }
    return function (value, flags) {
        var _a, _b;
        const options = flags & FLAG_MISSING_VALUE
            ? optionals
            : ((_b = (_a = byType === null || byType === void 0 ? void 0 : byType[toInputType(value)]) !== null && _a !== void 0 ? _a : litMap === null || litMap === void 0 ? void 0 : litMap.get(value)) !== null && _b !== void 0 ? _b : unknowns);
        let count = 0;
        let issueTree = issue;
        for (let i = 0; i < options.length; i++) {
            const r = options[i].func(value, flags);
            if (r === undefined || r.ok) {
                return r;
            }
            issueTree = count > 0 ? joinIssues(issueTree, r) : r;
            count++;
        }
        if (count > 1) {
            return { ok: false, code: "invalid_union", tree: issueTree };
        }
        return issueTree;
    };
}
class UnionType extends Type {
    constructor(options) {
        super();
        this.options = options;
        this.name = "union";
    }
    toTerminals(func) {
        this.options.forEach((o) => {
            o.toTerminals(func);
        });
    }
    func(v, flags) {
        let func = this._func;
        if (func === undefined) {
            const flattened = [];
            this.options.forEach((option) => {
                option.toTerminals((terminal) => {
                    flattened.push({ root: option, terminal });
                });
            });
            const base = createUnionBaseMatcher(flattened);
            const object = createUnionObjectMatcher(flattened);
            if (!object) {
                func = base;
            }
            else {
                func = function (v, f) {
                    if (isObject(v)) {
                        return object(v, f);
                    }
                    return base(v, f);
                };
            }
            this._func = func;
        }
        return func(v, flags);
    }
}
const STRICT = Object.freeze({ mode: "strict" });
const STRIP = Object.freeze({ mode: "strip" });
const PASSTHROUGH = Object.freeze({ mode: "passthrough" });
class TransformType extends Type {
    constructor(transformed, transform) {
        super();
        this.transformed = transformed;
        this.transform = transform;
        this.name = "transform";
        this.undef = ok(undefined);
        this.transformChain = undefined;
        this.transformRoot = undefined;
    }
    func(v, flags) {
        let chain = this.transformChain;
        if (!chain) {
            chain = [];
            // eslint-disable-next-line @typescript-eslint/no-this-alias
            let next = this;
            while (next instanceof TransformType) {
                chain.push(next.transform);
                next = next.transformed;
            }
            chain.reverse();
            this.transformChain = chain;
            this.transformRoot = next;
        }
        let result = this.transformRoot.func(v, flags);
        if (result !== undefined && !result.ok) {
            return result;
        }
        let current;
        if (result !== undefined) {
            current = result.value;
        }
        else if (flags & FLAG_MISSING_VALUE) {
            current = undefined;
            result = this.undef;
        }
        else {
            current = v;
        }
        const options = flags & FLAG_FORBID_EXTRA_KEYS
            ? STRICT
            : flags & FLAG_STRIP_EXTRA_KEYS
                ? STRIP
                : PASSTHROUGH;
        for (let i = 0; i < chain.length; i++) {
            const r = chain[i](current, options);
            if (r !== undefined) {
                if (!r.ok) {
                    return r;
                }
                current = r.value;
                result = r;
            }
        }
        return result;
    }
    toTerminals(func) {
        this.transformed.toTerminals(func);
    }
}
class LazyType extends Type {
    constructor(definer) {
        super();
        this.definer = definer;
        this.name = "lazy";
        this.recursing = false;
    }
    func(v, flags) {
        if (!this.type) {
            this.type = this.definer();
        }
        return this.type.func(v, flags);
    }
    toTerminals(func) {
        if (this.recursing) {
            return;
        }
        try {
            this.recursing = true;
            if (!this.type) {
                this.type = this.definer();
            }
            this.type.toTerminals(func);
        }
        finally {
            this.recursing = false;
        }
    }
}
class NeverType extends Type {
    constructor() {
        super(...arguments);
        this.name = "never";
        this.issue = {
            ok: false,
            code: "invalid_type",
            expected: [],
        };
    }
    func(_, __) {
        return this.issue;
    }
}
const neverSingleton = new NeverType();
/**
 * Create a validator that never matches any value,
 * analogous to the TypeScript type `never`.
 */
function never() {
    return neverSingleton;
}
class UnknownType extends Type {
    constructor() {
        super(...arguments);
        this.name = "unknown";
    }
    func(_, __) {
        return undefined;
    }
}
const unknownSingleton = new UnknownType();
/**
 * Create a validator that matches any value,
 * analogous to the TypeScript type `unknown`.
 */
function unknown() {
    return unknownSingleton;
}
class UndefinedType extends Type {
    constructor() {
        super(...arguments);
        this.name = "undefined";
        this.issue = {
            ok: false,
            code: "invalid_type",
            expected: ["undefined"],
        };
    }
    func(v, _) {
        return v === undefined ? undefined : this.issue;
    }
}
const undefinedSingleton = new UndefinedType();
/**
 * Create a validator that matches `undefined`.
 */
function undefined_() {
    return undefinedSingleton;
}
class NullType extends Type {
    constructor() {
        super(...arguments);
        this.name = "null";
        this.issue = {
            ok: false,
            code: "invalid_type",
            expected: ["null"],
        };
    }
    func(v, _) {
        return v === null ? undefined : this.issue;
    }
}
const nullSingleton = new NullType();
/**
 * Create a validator that matches `null`.
 */
function null_() {
    return nullSingleton;
}
class NumberType extends Type {
    constructor() {
        super(...arguments);
        this.name = "number";
        this.issue = {
            ok: false,
            code: "invalid_type",
            expected: ["number"],
        };
    }
    func(v, _) {
        return typeof v === "number" ? undefined : this.issue;
    }
}
const numberSingleton = new NumberType();
/**
 * Create a validator that matches any number value.
 */
function number() {
    return numberSingleton;
}
class BigIntType extends Type {
    constructor() {
        super(...arguments);
        this.name = "bigint";
        this.issue = {
            ok: false,
            code: "invalid_type",
            expected: ["bigint"],
        };
    }
    func(v, _) {
        return typeof v === "bigint" ? undefined : this.issue;
    }
}
const bigintSingleton = new BigIntType();
/**
 * Create a validator that matches any bigint value.
 */
function bigint() {
    return bigintSingleton;
}
class StringType extends Type {
    constructor() {
        super(...arguments);
        this.name = "string";
        this.issue = {
            ok: false,
            code: "invalid_type",
            expected: ["string"],
        };
    }
    func(v, _) {
        return typeof v === "string" ? undefined : this.issue;
    }
}
const stringSingleton = new StringType();
/**
 * Create a validator that matches any string value.
 */
function string() {
    return stringSingleton;
}
class BooleanType extends Type {
    constructor() {
        super(...arguments);
        this.name = "boolean";
        this.issue = {
            ok: false,
            code: "invalid_type",
            expected: ["boolean"],
        };
    }
    func(v, _) {
        return typeof v === "boolean" ? undefined : this.issue;
    }
}
const booleanSingleton = new BooleanType();
/**
 * Create a validator that matches any boolean value.
 */
function boolean() {
    return booleanSingleton;
}
class LiteralType extends Type {
    constructor(value) {
        super();
        this.value = value;
        this.name = "literal";
        this.issue = {
            ok: false,
            code: "invalid_literal",
            expected: [value],
        };
    }
    func(v, _) {
        return v === this.value ? undefined : this.issue;
    }
}
/**
 * Create a validator for a specific string, number, bigint or boolean value.
 */
function literal(value) {
    return new LiteralType(value);
}
/**
 * Create a validator for an object type.
 */
function object(obj) {
    return new ObjectType(obj, undefined);
}
/**
 * Create a validator for a record type `Record<string, T>`,
 * where `T` is the output type of the given subvalidator.
 */
function record(valueType) {
    return new ObjectType({}, valueType !== null && valueType !== void 0 ? valueType : unknown());
}
/**
 * Create a validator for an array type `T[]`,
 * where `T` is the output type of the given subvalidator.
 */
function array(item) {
    return new ArrayOrTupleType([], item !== null && item !== void 0 ? item : unknown(), []);
}
/**
 * Create a validator for an array type `[T1, T2, ..., Tn]`,
 * where `T1`, `T2`, ..., `Tn` are the output types of the given subvalidators.
 */
function tuple(items) {
    return new ArrayOrTupleType(items, undefined, []);
}
/**
 * Create a validator that matches any type `T1 | T2 | ... | Tn`,
 * where `T1`, `T2`, ..., `Tn` are the output types of the given subvalidators.
 *
 * This is analogous to how TypeScript's union types are constructed.
 */
function union(...options) {
    return new UnionType(options);
}
/**
 * Create a validator that can reference itself, directly or indirectly.
 *
 * In most cases an explicit type annotation is also needed, as TypeScript
 * cannot infer return types of recursive functions.
 *
 * @example
 * ```ts
 * import * as v from "@badrap/valita";
 *
 * type T = string | T[];
 * const type: v.Type<T> = v.lazy(() => v.union(v.string(), v.array(type)));
 * ```
 */
function lazy(definer) {
    return new LazyType(definer);
}
export { never, unknown, number, bigint, string, boolean, object, record, array, tuple, literal, union, null_ as null, undefined_ as undefined, lazy, ok, err, };
//# sourceMappingURL=index.js.map